import { core } from './client';
import type {
  ConfigureInstitutionRequest,
  InstitutionConfigurationRecord,
  ActivationResult,
  ConfigurationKey,
  ConfigurationKeysResponse,
  ConfigurationLayer,
  ConfigurationLayerScope,
  ConfigurationOverridesRequest,
  ConfigurationVersion,
  ResolvedConfiguration,
  ResolvedConfigurationValue,
  UpsertConfigurationLayerRequest,
} from '@/types/studio';

/**
 * The Configuration Studio's calls, in one file.
 *
 * ROUTING. Everything here is served by ConfigurationVersionController in
 * campusone-core-service, mounted at `${PLATFORM_ADMIN_ROUTE_PREFIX}/institution-configurations`
 * — the prefix resolves to 'platform', giving `/platform/institution-configurations/...`
 * behind the BFF's core upstream. The paths below are transcribed from that controller, not
 * from a hopeful contract; test/studio-api.test.ts pins them so a controller move fails a
 * test here instead of becoming a permanently wrong "not available yet" panel.
 *
 * VALIDATED AT THE BOUNDARY, like src/api/experience.ts. A resolved configuration whose
 * `values` is not a record would reach the provenance ladder and blank the page three renders
 * later, where the stack names a component instead of an endpoint. Malformed bodies throw
 * HERE, so AsyncPanel can say which call broke — and an unreadable response is never rendered
 * as an empty studio, because "the server answered garbage" and "there is nothing configured"
 * are different statements and only one of them is actionable.
 */

const BASE = '/platform/institution-configurations';

const seg = encodeURIComponent;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function fail(what: string): never {
  throw new Error(`The server returned ${what} this app cannot read.`);
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

/**
 * A provenance source: the four fields the ladder renders. `layerId` may be null (override
 * scopes have no layer row), so only its PRESENCE as string-or-null is enforced.
 */
function isSource(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.scope === 'string' &&
    typeof value.scopeKey === 'string' &&
    typeof value.label === 'string'
  );
}

/**
 * One resolved value with provenance. The winner's source is required — a value nobody can
 * say the origin of defeats the purpose of this screen — while `overriddenLayers` tolerates
 * junk entries by dropping them: a half-readable ladder still explains more than no ladder.
 */
function resolvedValue(value: unknown): ResolvedConfigurationValue {
  if (!isRecord(value) || typeof value.key !== 'string' || typeof value.value !== 'string') {
    fail('a resolved configuration value');
  }
  if (!isSource(value.source)) fail('a resolved value with no source layer');
  const source = value.source as ResolvedConfigurationValue['source'];
  return {
    key: value.key as ConfigurationKey,
    value: value.value,
    source,
    campusOverridable: value.campusOverridable === true,
    campusOverridableSource: isSource(value.campusOverridableSource)
      ? (value.campusOverridableSource as ResolvedConfigurationValue['source'])
      : null,
    overriddenLayers: Array.isArray(value.overriddenLayers)
      ? (value.overriddenLayers.filter(isSource) as ResolvedConfigurationValue['source'][])
      : [],
    note: typeof value.note === 'string' && value.note.length > 0 ? value.note : null,
  };
}

/**
 * A full resolution. `values` is rebuilt entry by entry rather than cast, because every
 * entry feeds the ladder and one malformed key must not take the other eight down with it —
 * it is dropped, and the key renders as "not resolved" rather than crashing the panel.
 */
function resolution(payload: unknown): ResolvedConfiguration {
  if (!isRecord(payload) || !isRecord(payload.values) || !isRecord(payload.operatingModel)) {
    fail('a resolved configuration');
  }
  const values: ResolvedConfiguration['values'] = {};
  for (const entry of Object.values(payload.values)) {
    try {
      const parsed = resolvedValue(entry);
      values[parsed.key] = parsed;
    } catch {
      // One unreadable entry is dropped; the page shows the key as unresolved.
    }
  }
  const model: Record<string, string> = {};
  for (const [field, value] of Object.entries(payload.operatingModel)) {
    if (typeof value === 'string') model[field] = value;
  }
  return {
    institutionId: typeof payload.institutionId === 'string' ? payload.institutionId : '',
    campusId: typeof payload.campusId === 'string' ? payload.campusId : null,
    versionId: typeof payload.versionId === 'string' ? payload.versionId : null,
    versionNumber: typeof payload.versionNumber === 'number' ? payload.versionNumber : null,
    operatingModel: model,
    values,
    resolvedAt: typeof payload.resolvedAt === 'string' ? payload.resolvedAt : '',
  };
}

/**
 * A version row. Only what the studio dereferences unconditionally is required; everything
 * else defaults to its honest empty value so a lean projection still renders.
 */
function version(payload: unknown): ConfigurationVersion {
  if (
    !isRecord(payload) ||
    typeof payload.id !== 'string' ||
    typeof payload.versionNumber !== 'number' ||
    typeof payload.state !== 'string'
  ) {
    fail('a configuration version');
  }
  const optional = (value: unknown): string | null => (typeof value === 'string' ? value : null);
  return {
    id: payload.id,
    institutionId: optional(payload.institutionId) ?? '',
    versionNumber: payload.versionNumber,
    state: payload.state as ConfigurationVersion['state'],
    institutionOverrides: isRecord(payload.institutionOverrides)
      ? (payload.institutionOverrides as ConfigurationVersion['institutionOverrides'])
      : {},
    campusOverrides: isRecord(payload.campusOverrides)
      ? (payload.campusOverrides as ConfigurationVersion['campusOverrides'])
      : {},
    resolvedOperatingModel: isRecord(payload.resolvedOperatingModel)
      ? (payload.resolvedOperatingModel as ConfigurationVersion['resolvedOperatingModel'])
      : null,
    provenance: isRecord(payload.provenance)
      ? (payload.provenance as ConfigurationVersion['provenance'])
      : null,
    resolvedCampusModels: isRecord(payload.resolvedCampusModels)
      ? (payload.resolvedCampusModels as ConfigurationVersion['resolvedCampusModels'])
      : {},
    validationErrors: stringArray(payload.validationErrors),
    sourceLayerIds: stringArray(payload.sourceLayerIds),
    changeReason: optional(payload.changeReason),
    createdBy: optional(payload.createdBy),
    approvedBy: optional(payload.approvedBy),
    approvedAt: optional(payload.approvedAt),
    effectiveFrom: optional(payload.effectiveFrom),
    activatedAt: optional(payload.activatedAt),
    supersededAt: optional(payload.supersededAt),
    supersededByVersionId: optional(payload.supersededByVersionId),
    previousVersionId: optional(payload.previousVersionId),
    createdAt: optional(payload.createdAt) ?? '',
    updatedAt: optional(payload.updatedAt) ?? '',
  };
}

function versionList(payload: unknown): ConfigurationVersion[] {
  if (!Array.isArray(payload)) fail('a configuration version list');
  // A junk row is dropped rather than sinking the list: eleven readable versions
  // and one broken one should render as eleven versions, not as an error page.
  return payload.flatMap((entry) => {
    try {
      return [version(entry)];
    } catch {
      return [];
    }
  });
}

function layer(payload: unknown): ConfigurationLayer {
  if (
    !isRecord(payload) ||
    typeof payload.id !== 'string' ||
    typeof payload.scope !== 'string' ||
    typeof payload.scopeKey !== 'string' ||
    typeof payload.label !== 'string'
  ) {
    fail('a configuration layer');
  }
  return {
    id: payload.id,
    scope: payload.scope as ConfigurationLayer['scope'],
    scopeKey: payload.scopeKey,
    label: payload.label,
    values: isRecord(payload.values) ? (payload.values as ConfigurationLayer['values']) : {},
    enabled: payload.enabled !== false,
    updatedBy: typeof payload.updatedBy === 'string' ? payload.updatedBy : null,
    createdAt: typeof payload.createdAt === 'string' ? payload.createdAt : '',
    updatedAt: typeof payload.updatedAt === 'string' ? payload.updatedAt : '',
  };
}

function layerList(payload: unknown): ConfigurationLayer[] {
  if (!Array.isArray(payload)) fail('a configuration layer list');
  return payload.flatMap((entry) => {
    try {
      return [layer(entry)];
    } catch {
      return [];
    }
  });
}

/**
 * GET /keys. The registry is the studio's source of truth for what may be picked — every
 * value input on every screen is a Select constrained to `allowedValues`, never free text,
 * so a typo cannot exist long enough to be refused.
 */
function keysResponse(payload: unknown): ConfigurationKeysResponse {
  if (!isRecord(payload) || !Array.isArray(payload.keys)) fail('the configuration key registry');
  const keys = payload.keys.flatMap((entry) => {
    if (
      !isRecord(entry) ||
      typeof entry.key !== 'string' ||
      typeof entry.label !== 'string' ||
      !Array.isArray(entry.allowedValues)
    ) {
      return [];
    }
    return [
      {
        key: entry.key as ConfigurationKey,
        label: entry.label,
        allowedValues: stringArray(entry.allowedValues),
        campusOverridableByDefault: entry.campusOverridableByDefault === true,
      },
    ];
  });
  if (keys.length === 0) fail('an empty configuration key registry');
  return {
    precedence: typeof payload.precedence === 'string' ? payload.precedence : '',
    keys,
  };
}

function activation(payload: unknown): ActivationResult {
  if (!isRecord(payload)) fail('an activation result');
  return {
    activated: version(payload.activated),
    superseded: payload.superseded == null ? null : version(payload.superseded),
  };
}

function body(payload: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(payload) };
}

/**
 * The onboarding half: whether this institution HAS a configuration record at all.
 * An institution created before the configuration work has none, and until one is
 * recorded there is nothing for the resolver to resolve against — the record's
 * type, subsystem and country are what select the layers.
 */
function configurationRecord(payload: unknown): InstitutionConfigurationRecord {
  if (!isRecord(payload) || typeof payload.institutionId !== 'string') fail('a configuration record');
  return {
    institutionId: payload.institutionId,
    institutionType: typeof payload.institutionType === 'string' ? payload.institutionType : '',
    educationSubsystem:
      typeof payload.educationSubsystem === 'string' ? payload.educationSubsystem : '',
    ownershipModel: typeof payload.ownershipModel === 'string' ? payload.ownershipModel : '',
    countryCode: typeof payload.countryCode === 'string' ? payload.countryCode : '',
    // The EduCore hand-off's outcome, carried so the console can answer "did the
    // academic side actually receive this?" instead of leaving it to a log grep.
    academicSeedStatus:
      typeof payload.academicSeedStatus === 'string' ? payload.academicSeedStatus : 'PENDING',
    academicSeedAt: typeof payload.academicSeedAt === 'string' ? payload.academicSeedAt : null,
    academicSeedError:
      typeof payload.academicSeedError === 'string' ? payload.academicSeedError : null,
  };
}

export const studioApi = {
  keys: () => core<unknown>(`${BASE}/keys`).then(keysResponse),

  /** 404 means "never configured", which the Studio treats as a state, not a failure. */
  configuration: (institutionId: string) =>
    core<unknown>(`${BASE}/${seg(institutionId)}`).then(configurationRecord),

  retrySeeding: (institutionId: string) =>
    core<unknown>(`${BASE}/${seg(institutionId)}/academic-seeding/retry`, body({})).then(
      (result) => {
        if (!isRecord(result)) fail('a seeding result');
        return {
          academicSeedAttempted: result.academicSeedAttempted === true,
          warnings: stringArray(result.warnings),
        };
      },
    ),

  configure: (payload: ConfigureInstitutionRequest) =>
    core<unknown>(`${BASE}`, body(payload)).then((result) => {
      if (!isRecord(result)) fail('a configuration result');
      return {
        created: result.created === true,
        changed: result.changed === true,
        academicSeedAttempted: result.academicSeedAttempted === true,
        warnings: stringArray(result.warnings),
      };
    }),

  layers: {
    list: (scope?: ConfigurationLayerScope) =>
      core<unknown>(`${BASE}/layers${scope ? `?scope=${seg(scope)}` : ''}`).then(layerList),
    upsert: (scope: ConfigurationLayerScope, scopeKey: string, payload: UpsertConfigurationLayerRequest) =>
      core<unknown>(`${BASE}/layers/${seg(scope)}/${seg(scopeKey)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }).then(layer),
  },

  versions: {
    list: (institutionId: string) =>
      core<unknown>(`${BASE}/${seg(institutionId)}/versions`).then(versionList),
    active: (institutionId: string) =>
      core<unknown>(`${BASE}/${seg(institutionId)}/versions/active`).then(version),
    createDraft: (institutionId: string, payload: ConfigurationOverridesRequest) =>
      core<unknown>(`${BASE}/${seg(institutionId)}/versions`, body(payload)).then(version),
    updateDraft: (institutionId: string, versionId: string, payload: ConfigurationOverridesRequest) =>
      core<unknown>(`${BASE}/${seg(institutionId)}/versions/${seg(versionId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }).then(version),
    preview: (institutionId: string, versionId: string) =>
      core<unknown>(`${BASE}/${seg(institutionId)}/versions/${seg(versionId)}/preview`).then(resolution),
    submit: (institutionId: string, versionId: string) =>
      core<unknown>(`${BASE}/${seg(institutionId)}/versions/${seg(versionId)}/submit`, body({})).then(version),
    approve: (institutionId: string, versionId: string) =>
      core<unknown>(`${BASE}/${seg(institutionId)}/versions/${seg(versionId)}/approve`, body({})).then(version),
    schedule: (institutionId: string, versionId: string, effectiveFrom: string) =>
      core<unknown>(
        `${BASE}/${seg(institutionId)}/versions/${seg(versionId)}/schedule`,
        body({ effectiveFrom }),
      ).then(version),
    activate: (institutionId: string, versionId: string) =>
      core<unknown>(`${BASE}/${seg(institutionId)}/versions/${seg(versionId)}/activate`, body({})).then(
        activation,
      ),
  },

  /** The operating model in force, with provenance. `campusId` re-resolves for one campus. */
  resolved: (institutionId: string, campusId?: string) =>
    core<unknown>(
      `${BASE}/${seg(institutionId)}/resolved${campusId ? `?campusId=${seg(campusId)}` : ''}`,
    ).then(resolution),
};
